// 9fbef606107a605d69c0edbcd8029e5d
import { createStore, applyMiddleware, compose, combineReducers } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/logOnlyInProduction';
import createSagaMiddleware from 'redux-saga';
import { cacheEnhancerMiddleware } from '@tcp/core/src/utils/cache.util';
import { setStoreRef } from '@tcp/core/src/utils/store.utils';
import globalSagas from '../sagas';
import globalReducers from '../reducers';
import createAnalyticsMiddleware from '../middlewares/analytics';
import createDataLayer from '../../analytics/dataLayer';

function createReducer(asyncReducers) {
  return combineReducers({
    ...globalReducers,
    ...asyncReducers,
  });
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const configureStore = (preloadedState) => {
  /**
   * Recreate the stdChannel (saga middleware) with every context.
   */

  const sagaMiddleware = createSagaMiddleware();

  const middlewares = [
    sagaMiddleware,
    // Use analytics middleware conditionally
    process.env.ANALYTICS && createAnalyticsMiddleware(),
  ];

  const enhancers = [applyMiddleware(...middlewares), cacheEnhancerMiddleware()];

  // Choose compose method depending upon environment and platform
  const composeEnhancers = typeof window === 'object' ? composeWithDevTools : compose;

  /**
   * Since Next.js does server-side rendering, you are REQUIRED to pass
   * `preloadedState` when creating the store.
   */

  const store = createStore(createReducer(), preloadedState, composeEnhancers(...enhancers));
  store.asyncReducers = {};
  store.asyncSaga = {};
  /**
   * function to inject the reducer dynamically when an object with key and reducer or and array is passed.
   * param - {key: Name of the reducer in the redux state, reducer: Reducer to be injected}
   * or an array of this object with key and reducer.
   * eg: {key: 'QuickView', reducer: QuickViewReducer}
   * or [{key: 'QuickView', reducer: QuickViewReducer}, {key: 'WriteReview', reducer: WriteReviewReducer}]
   */
  store.injectReducer = (asyncReducer) => {
    if (Array.isArray(asyncReducer)) {
      let isNewKey = false;
      asyncReducer.forEach((reducerObj) => {
        const { key, reducer } = reducerObj;
        if (!globalReducers[key] && !store.asyncReducers[key]) {
          store.asyncReducers[key] = reducer;
          if (!isNewKey) {
            isNewKey = true;
          }
        }
      });
      if (!isNewKey) return;
    } else {
      const { key, reducer } = asyncReducer;
      if (globalReducers[key] || store.asyncReducers[key]) {
        return;
      }
      store.asyncReducers[key] = reducer;
    }
    store.replaceReducer(createReducer(store.asyncReducers));
  };
  /**
   * function to inject the saga dynamically when an object with key and saga or and array is passed.
   * param - {key: Unique string or saga name(can be same as reducer key), saga: saga to be injected}
   * or an array of this object with key and reducer.
   * eg: {key: 'QuickView', reducer: QuickViewSaga}
   * or [{key: 'QuickView', reducer: QuickViewSaga}, {key: 'WriteReview', reducer: WriteReviewSaga}]
   */
  store.injectSaga = (asyncSaga) => {
    if (Array.isArray(asyncSaga)) {
      asyncSaga.forEach((sagaObj) => {
        const { key, saga } = sagaObj;
        if (key && !store.asyncSaga[key]) {
          store.asyncSaga[key] = saga;
          sagaMiddleware.run(saga);
        }
      });
    } else {
      const { key, saga } = asyncSaga;
      if (store.asyncSaga[key] || !asyncSaga) {
        return;
      }
      store.asyncSaga[key] = saga;
      sagaMiddleware.run(saga);
    }
  };
  /**
   * next-redux-saga depends on `sagaTask` being attached to the store.
   * It is used to await the rootSaga task before sending results to the client.
   */
  store.sagaTask = sagaMiddleware.run(globalSagas);

  // Need to save the store in a separate variable as there is no easy way of getting the store in Non-saga file like util.js
  setStoreRef(store);

  /**
   * This is where we assign window._dataLayer for analytics logic
   */
  if (process.env.ANALYTICS && typeof window !== 'undefined') {
    // eslint-disable-next-line
    window._dataLayer = createDataLayer(store);
  }

  return store;
};

export default configureStore;
